<template>
  <LayoutRegistration :small-logo="true" :heading="heading" :subheading="subheading">
    <template #main>
      <div v-if="!success">
        <validation-observer
          v-slot="{ errors, invalid }"
          ref="observer"
          @submit.prevent="submit"
          tag="form"
          class="Form-light text-left relative"
          autocomplete="off"
        >
          <validation-provider rules="required|email" :name="$t('regWizPopUp.forms.emailAddress')" v-slot="{ errors }" slim>
            <form-text-input
              v-model="email"
              input-id="emailAddress"
              :input-label="$t('regWizPopUp.forms.emailAddress')"
              input-name="emailAddress"
              :error="errors[0]" />
          </validation-provider>

          <validation-provider rules="required" :name="$t('regWizPopUp.forms.subject')" v-slot="{ errors }" slim>
            <form-text-input
              v-model="subject"
              input-id="subject"
              :input-label="$t('regWizPopUp.forms.subject')"
              input-name="subject"
              :error="errors[0]" />
          </validation-provider>

          <validation-provider rules="required" :name="$t('regWizPopUp.forms.message')" v-slot="{ errors }" slim>
            <div class="relative mb-4">
              <label for="Message">
                {{ $t('regWizPopUp.forms.message') }} <span class="text-orange-dark">*</span>
              </label>
              <textarea v-model="message" name="Message" id="Message"></textarea>
              <span v-if="errors[0]" class="text-red text-xs font-semibold">{{ errors[0] }}</span>
            </div>
          </validation-provider>

          <!-- prevention -->
          <div style="position: absolute; z-index: -1; top: -99999px; right: -99999px;">
            <form-text-input
              v-model="comments"
              input-id="comments"
              :input-label="$t('regWizPopUp.forms.comments')"
              input-name="comments"
              :inputRequired="false"
              :inputTabindex="-1"
              :inputTitle="$t('regWizPopUp.forms.honeyPotCopy')" />
          </div>

          <p v-if="guid"><small>{{ $t('regWizPopUp.routes.troubleshoot.relevantServerData') }}</small></p>

          <hr class="w-full h-1px my-6 bg-grey dark:bg-grey-dark shadow-none border-none rounded-none" />

          <footer class="flex flex-col mt-6">
            <p v-if="localError" class="text-red font-semibold text-right mb-6">{{ localError }}</p>
            <div
              class="flex flex-wrap sm:flex-nowrap justify-end items-center"
              :class="{
                'sm:justify-between': guid,
              }"
            >
              <a
                v-if="guid && !prevRoute.includes('Success')"
                @click="$router.go(-1)"
                class="cursor-pointer text-sm text-orange-dark hover:text-orange focus:text-orange underline order-2 sm:order-1"
              >{{ $t('regWizPopUp.goBack') }}</a>
              <button :disabled="invalid || loading" type="submit" class="button w-full sm:w-auto order-1 sm:order-2 mb-6 sm:mb-0">{{ submitText }}</button>
            </div>
          </footer>
        </validation-observer>
      </div>
    </template>
    <template #footer>
      <button
        v-if="success"
        @click="closePopUpFromPopUp()"
        class="text-sm text-grey-lightest hover:text-white focus:text-white underline"
      >{{ $t('closePopUp') }}</button>
    </template>
  </LayoutRegistration>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import transform from 'lodash/transform';
import isEqual from 'lodash/isEqual';
import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';
import semverGte from 'semver/functions/gte';
import { extend, ValidationObserver, ValidationProvider } from 'vee-validate';
import { email, required } from 'vee-validate/dist/rules';

import { log } from '../../log';
import { SAFE_JSON_PARSE, OBJ_TO_STR, WEBGUI_PARENT } from '../../consts';
import { KeyServer } from '../../services';
import i18n from '../../i18n';

import LayoutRegistration from '../../layouts/Registration.vue';
import FormTextInput from '../../components/Form/TextInput.vue';

extend('email', email);
extend('required', {
  ...required,
  message: i18n.tc('regWizPopUp.forms.fieldRequired'),
});
export default {
  name: 'ViewTroubleshoot',
  metaInfo: {
    title: i18n.tc('regWizPopUp.routes.troubleshoot.heading.default'),
  },
  components: {
    LayoutRegistration,
    ValidationObserver,
    ValidationProvider,
    FormTextInput,
  },
  data: () => ({
    localError: null,
    loading: false,
    success: false,
    email: '', // this will be automatically set on load if this.userEmail exists
    subject: '',
    message: '',
    prevRoute: '',
    comments: '', // honeypot field
  }),
  computed: {
    ...mapGetters([
      'server',
      'stateData',
    ]),
    ...mapState([
      'apiReport',
      'apiVersion',
      'errorMessage',
      'errorMessageSubtext',
      'guid',
      'keyfile',
      'postMessageError',
      'rawServerState',
      'state',
      'userEmail',
    ]),
    sanitizedRawServerStateForEmail() { // sanitization of specific values we don't want to send via email to support or doubled up values
      if (!this.rawServerState) return null;
      const mutated = this.rawServerState;
      ['internalport', 'keyfile', 'plgPath', 'protocol'].map(v => delete mutated[v]);
      return mutated;
    },
    mutatedStateValues() { // compare the rawServerState received from the initla postMessage on pop-up creation and compare it to the pop-up's current values then extract those values to send via support email
      return this.rawServerState ? this.difference(this.rawServerState, this.server) : null;
    },
    // strips HTML from Error Message Subtext @see: https://css-tricks.com/snippets/javascript/strip-html-tags-in-javascript/
    finalMessage() {
      return `${this.message}\n\n\n`
        + '*******************\n'
        + '*** Pop-up Info ***\n'
        + '*******************\n'
        + `${this.errorMessage ? `RegWiz App Error Message: ${this.errorMessage}\n` : ''}`
        + `${this.errorMessageSubtext ? `RegWiz App Error Message Subtext: ${this.errorMessageSubtext.replace(/(<([^>]+)>)/gi, '')}\n` : ''}`
        + `${this.prevRoute ? `Previous Page: ${this.prevRoute}\n` : ''}`
        + `Browser Info: ${navigator.userAgent}\n`
        + `${this.stateData.error ? '***********************************\n' : ''}`
        + `${this.stateData.error ? `*** State Data ${this.state} ***\n` : ''}`
        + `${this.stateData.error ? '***********************************\n' : ''}`
        + `${this.stateData.error ? OBJ_TO_STR(this.stateData).replace('error: [object Object]\n', '') : ''}`
        + `${this.stateData.error ? '***********************************\n' : ''}`
        + `${this.stateData.error ? `*** ${this.state} ERROR DETAILS ***\n` : ''}`
        + `${this.stateData.error ? '***********************************\n' : ''}`
        + `${this.stateData.error ? OBJ_TO_STR(this.stateData.error) : ''}`
        + `${this.postMessageError ? '***************************\n' : ''}`
        + `${this.postMessageError ? '*** Post Message Error  ***\n' : ''}`
        + `${this.postMessageError ? '***************************\n' : ''}`
        + `${this.postMessageError ? OBJ_TO_STR(this.postMessageError) : ''}`
        + `${this.mutatedStateValues ? '**********************************************\n' : ''}`
        + `${this.mutatedStateValues ? '*** Server state values changed in pop-up ***\n' : ''}`
        + `${this.mutatedStateValues ? '**********************************************\n' : ''}`
        + `${this.mutatedStateValues ? OBJ_TO_STR(this.mutatedStateValues) : ''}`
        + `${this.sanitizedRawServerStateForEmail ? '*******************************\n' : ''}`
        + `${this.sanitizedRawServerStateForEmail ? '*** Raw server state values ***\n' : ''}`
        + `${this.sanitizedRawServerStateForEmail ? '*******************************\n' : ''}`
        + `${this.sanitizedRawServerStateForEmail ? OBJ_TO_STR(this.sanitizedRawServerStateForEmail) : ''}`;
    },
    formData() {
      return {
        email: this.email,
        // prefix subjects for submissions
        subject: this.subject,
        message: this.finalMessage,
        guid: this.guid,
        comments: this.comments,
        keyfile: this.keyfile,
      };
    },
    heading() {
      return this.success
        ? this.$t('regWizPopUp.routes.troubleshoot.heading.success')
        : this.$t('regWizPopUp.routes.troubleshoot.heading.default');
    },
    subheading() {
      if ((this.errorMessage || this.errorMessageSubtext) && !this.success) {
        // if chrome extension error present only this error
        if (this.errorMessageSubtext && this.errorMessageSubtext.includes('Chrome extension')) return `<span class="block mt-2 text-red">App Error Detected.<br>${this.errorMessageSubtext}</span>`;
        // otherwise show error and sub error
        return `${this.$t('regWizPopUp.routes.troubleshoot.subheading.default')}<span class="block mt-2 text-red">App Error Detected. ${this.errorMessage}${this.errorMessageSubtext ? `<span class="block mt-2">${this.errorMessageSubtext}</span>` : ''}</span>`;
      }
      return this.success
        ? this.$t('regWizPopUp.routes.troubleshoot.subheading.success')
        : 'Followed the steps on the Depcreation Notice and still having issues? We apologize for the trouble. Please contact support and we will get things sorted for you as soon as we can.'; // this.$t('regWizPopUp.routes.troubleshoot.subheading.default')
    },
    submitText() {
      return this.loading ? this.$t('regWizPopUp.forms.submitting') : this.$t('regWizPopUp.forms.submit');
    },
  },
  methods: {
    ...mapActions(['closePopUpFromPopUp']),
    async submit() {
      // reset
      this.localError = null;
      this.loading = true;
      const isValid = await this.$refs.observer.validate();
      if (!isValid) {
        this.localError = this.$t('regWizPopUp.forms.notValid');
        this.loading = false;
        return log.error(this.localError);
      }
      // 🐿 ship it
      log.debug('[Troubleshoot Form Submit] %o', this.formData);
      return KeyServer.troubleshoot(this.formData)
        .json()
        .then(() => {
          this.loading = false;
          this.success = true;
        })
        .catch((error) => {
          // eslint-disable-next-line no-unused-vars
          const [err, data] = SAFE_JSON_PARSE(error.message);
          if (data) {
            this.localError = data.errorMessage ? data.errorMessage : data.error;
          } else {
            this.localError = error;
          }
          log.error(this.localError);
          this.loading = false;
        });
    },
    preventClose(e) {
      if (this.loading) {
        e.preventDefault();
        // eslint-disable-next-line no-param-reassign
        e.returnValue = '';
        // eslint-disable-next-line no-alert
        alert(this.$t('regWizPopUp.closingPopUpMayLeadToErrors'));
      }
    },
    difference(origObj, newObj) { // @see – https://davidwells.io/snippets/get-difference-between-two-objects-javascript
      const changes = (mutatedObj = newObj, rawObj = origObj) => {
        let arrayIndexCounter = 0;
        return transform(mutatedObj, (result, value, key) => {
          if (!isEqual(value, rawObj[key])) {
            // eslint-disable-next-line no-plusplus
            const resultKey = isArray(rawObj) ? arrayIndexCounter++ : key;
            // eslint-disable-next-line no-param-reassign
            result[resultKey] = (isObject(value) && isObject(rawObj[key])) ? changes(value, rawObj[key]) : value;
          }
        });
      };
      return changes(newObj, origObj);
    },
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      // eslint-disable-next-line no-param-reassign
      if (vm.prevRoute) vm.prevRoute = from.name;
    });
  },
  created() {
    if (this.userEmail) this.email = this.userEmail; // auto fills the email field from the email provided by the server state
    // auto fill the subject field
    // if (this.stateData.error) this.subject = this.stateData.error.heading; // turn off for deprecation
    if (this.errorMessage) this.subject = this.errorMessage; // app errorMessage takes precedence over server state error
    // if the subtext message is for the chrome extension let's set subject to that
    if (this.errorMessage && this.errorMessageSubtext && this.errorMessageSubtext.includes('Chrome extension')) {
      const splt = this.errorMessageSubtext.split(' <span');
      log.log('[splt]', splt, splt[0]);
      // eslint-disable-next-line prefer-destructuring
      this.subject = splt[0];
    }
    // prefix subjects for submissions
    this.subject = `[Deprecation Notice Account Help] ${this.subject}`;
    // prevent pop-up from closing before form is done submitting
    window.addEventListener('beforeunload', this.preventClose);
    // if the user has the api we ask for the api to run a report
    if (this.apiVersion && semverGte(this.apiVersion, '2.41.1')) {
      // this will receive `API_EXECUTE_PINGBACK` in response from the UPC
      WEBGUI_PARENT.postMessage(JSON.stringify({ event: 'REQUEST_API_REPORT' }), '*');
    }
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.preventClose);
  },
};
</script>
